'use client';

import Link from 'next/link';
import { format } from 'date-fns';

import { Button } from '@/components/ui/forms/button';
import { EntityList } from '@/components/ui/design-system';
import { JobEntity } from '@/components/ui/design-system/entities/job/JobEntity';
import {
  groupedToJobEntityData,
  toJobEntityData,
} from '@/components/ui/design-system/entities/job/JobEntity.types';
import { GetContactsButton } from '@/components/features/reports/GetContactsButton';
import { groupJobsByCompanyAndTitle } from '@/utils/groupJobs';
import { generateJobDetailPath } from '@/utils/jobs';
import type { Job } from '@/types/jobs-types';
import { cn } from '@/lib/utils';

/**
 * Shared jobs list — used by /brief, /dashboard/jobs, and /dashboard/jobs/search.
 *
 * The cards mode uses the EntityList + JobEntity stack with thin overlapping
 * borders (`gap-0 [&>*+*]:-mt-px`), so adjacent cards share a single 1px line.
 * The table mode mirrors the existing table layout used elsewhere.
 *
 * The component is purely presentational — consumers manage view-mode state,
 * pagination, and surrounding chrome (SectionPanel, headers, tabs).
 */
export interface JobsListActions {
  apply?: boolean;
  contactHiringManager?: boolean;
  viewDetails?: boolean;
}

export interface JobsListProps {
  jobs: Job[];
  viewMode?: 'cards' | 'table';
  actions?: JobsListActions;
  density?: 'comfortable' | 'compact';
  loading?: boolean;
  emptyState?: React.ReactNode;
  /** Path prefix for job detail links (e.g. `/d/job` or `/job`). */
  detailBasePath?: string;
  /** Item key prefix; useful when multiple lists share the page. */
  itemKeyPrefix?: string;
  /** Optional per-item badge label (e.g. "Best Match" on index 0). */
  badgeFor?: (job: Job, index: number) => string | undefined;
  /** Optional tracking context, forwarded to JobEntity. */
  trackingContext?: string;
  cardsContainerClassName?: string;
  tableContainerClassName?: string;
}

const DEFAULT_ACTIONS: JobsListActions = {
  apply: true,
  contactHiringManager: true,
  viewDetails: false,
};

function getLocationString(job: Job): string {
  const city = job.city ?? '';
  const state = job.state ?? '';
  const country = job.country ?? '';
  return [city, state, country].filter(Boolean).join(', ');
}

function getJobKey(job: Job): string {
  const id = job.job_id || job.id || '';
  const fallbackLocation = getLocationString(job);
  return `${id}-${fallbackLocation}`;
}

function formatFreshnessDate(job: Job): string {
  // Prefer freshness ("last seen"), fall back to the original posting date.
  const value = job.last_seen_at || job.posted_date;
  if (!value) return 'Recently updated';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Recently updated';
  return format(date, 'MMM dd, yyyy');
}

export function JobsList({
  jobs,
  viewMode = 'cards',
  actions,
  density = 'comfortable',
  loading = false,
  emptyState,
  detailBasePath = '/d/job',
  itemKeyPrefix,
  badgeFor,
  trackingContext,
  cardsContainerClassName,
  tableContainerClassName,
}: JobsListProps) {
  const resolvedActions = { ...DEFAULT_ACTIONS, ...actions };

  if (!loading && jobs.length === 0) {
    return (
      <>
        {emptyState ?? (
          <div className="p-4 border border-border bg-card text-sm text-muted-foreground text-center">
            No jobs found.
          </div>
        )}
      </>
    );
  }

  if (viewMode === 'table') {
    return (
      <div
        className={cn('border border-border bg-card', tableContainerClassName)}
        style={{ boxShadow: 'var(--shadow-warm-sm)' }}
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse">
            <thead className="bg-muted/40">
              <tr className="text-left text-overline text-muted-foreground">
                <th className="p-3 border-b border-border">Role</th>
                <th className="p-3 border-b border-border">Company</th>
                <th className="p-3 border-b border-border">Location</th>
                <th className="p-3 border-b border-border">Updated</th>
                <th className="p-3 border-b border-border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => {
                const jobDetailPath = generateJobDetailPath(job, detailBasePath);
                const location = getLocationString(job);
                const jobUrl = job.job_url || job.click_url;
                const rowKey = `${itemKeyPrefix ?? 'jobs'}-${getJobKey(job)}`;

                return (
                  <tr key={rowKey} className="border-b border-border/60">
                    <td className="p-3 text-label text-foreground">{job.title}</td>
                    <td className="p-3 text-body text-muted-foreground">{job.company}</td>
                    <td className="p-3 text-body text-muted-foreground">{location || 'Remote'}</td>
                    <td className="p-3 text-caption text-muted-foreground">{formatFreshnessDate(job)}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {resolvedActions.viewDetails !== false && (
                          <Button asChild size="sm" variant="outline" className="h-8 px-3">
                            <Link href={jobDetailPath}>View</Link>
                          </Button>
                        )}
                        {resolvedActions.contactHiringManager && (
                          <GetContactsButton
                            type="job"
                            companyName={job.company}
                            jobTitle={job.title}
                            jobLocation={location}
                            jobUrl={job.job_url || job.click_url || undefined}
                            jobId={job.job_id || job.id}
                            jobDescription={job.description || undefined}
                            jobContextData={job as unknown as Record<string, unknown>}
                            size="sm"
                            className="h-8 px-3"
                            onClick={(event) => event.stopPropagation()}
                          >
                            Contact Hiring Manager
                          </GetContactsButton>
                        )}
                        {resolvedActions.apply && jobUrl && (
                          <Button asChild size="sm" className="h-8 px-3">
                            <a href={jobUrl} target="_blank" rel="noreferrer">
                              Apply
                            </a>
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <EntityList
      mode="separated"
      listClassName={cn('gap-0 [&>*+*]:-mt-px', cardsContainerClassName)}
      items={groupJobsByCompanyAndTitle(jobs)}
      itemKey={(groupedJob) =>
        `${itemKeyPrefix ?? 'jobs'}-${groupedJob.primaryJob.job_id || groupedJob.primaryJob.id}`
      }
      renderItem={(groupedJob, index) => {
        const data =
          groupedJob.locations.length > 1
            ? groupedToJobEntityData(groupedJob)
            : toJobEntityData(groupedJob.primaryJob);
        const badge = badgeFor?.(groupedJob.primaryJob, index);
        if (badge) data.badgeLabel = badge;

        return (
          <JobEntity
            job={data}
            layout="card"
            density={density}
            actions={resolvedActions}
            trackingContext={trackingContext}
          />
        );
      }}
    />
  );
}

export default JobsList;
